import { gql, TypedDocumentNode } from '@apollo/client';
import type {
  ClinicalReportQuery,
  ClinicalReportQueryVariables,
  GetMyRiskOrdersQuery,
  GetOrderDetailsQuery,
  GetOrderDetailsQueryVariables,
  GetOrderRequestResultsReleasedQuery,
  GetOrderRequestResultsReleasedQueryVariables,
  ReportsIDQuery,
  ReportsIDQueryVariables,
  ResultsOverviewQuery,
  ResultsOverviewQueryVariables,
} from 'gql/graphql';

export const GET_ORDERS = gql`
  query GetMyRiskOrders {
    orders {
      __typename
      ... on OrdersListSuccess {
        ordersList {
          externalId
          accessionId
          createDate
          hasReleasedResults
          product
          patientFirstName
          patientLastName

          sampleTracker {
            statusSummary
            sampleName
            lastUpdated
            oneStepStatusTracker {
              overallStatus
              internalFlowStatus
              isMerged
            }
            statusTrackers {
              patientName
              trackerName
              icon
              trackerSteps {
                name
                status
                label
                description
                stepDate
              }
            }
          }
        }
      }
    }
  }
` as TypedDocumentNode<GetMyRiskOrdersQuery>;

export const RESULTS_OVERVIEW = gql`
  query ResultsOverview($orderRequestId: String!) {
    resultsOverview(orderRequestId: $orderRequestId) {
      __typename
      ... on ResultsOverviewSuccess {
        overallSeverity
        resultsReleased
        product {
          id
          name
          slug
        }
        patient {
          email
          phone
          firstName
          lastName
          accessionId
        }
        tests {
          testName
          testStatus
          testSeverity
          reportId
          resultsReleasedToPatient
          resultsViewedByPatient
        }
      }
      ... on ReportNotReleased {
        status
        message
      }
      ... on UserNotFound {
        status
        message
      }
      ... on NotFound {
        status
        message
      }
    }
  }
` as TypedDocumentNode<ResultsOverviewQuery, ResultsOverviewQueryVariables>;

export const GET_CLINICAL_REPORT_IDS = gql`
  query ReportsID($orderRequestId: String!) {
    getReportIds(orderRequestId: $orderRequestId) {
      __typename
      ... on GetReportIdsSuccess {
        reportIds
      }
    }
  }
` as TypedDocumentNode<ReportsIDQuery, ReportsIDQueryVariables>;

export const GET_CLINICAL_REPORT = gql`
  query ClinicalReport($reportUuid: String!, $orderRequestId: String!) {
    getClinicalReport(
      reportUuid: $reportUuid
      orderRequestId: $orderRequestId
    ) {
      __typename
      ... on ReportSuccess {
        contentType
        filename
        report
      }
    }
  }
` as TypedDocumentNode<ClinicalReportQuery, ClinicalReportQueryVariables>;

export const GET_ORDER_DETAILS = gql`
  query GetOrderDetails($externalId: String!) {
    orderDetails(externalId: $externalId) {
      __typename
      ... on OrderDetailsSuccess {
        detail {
          accessionDate
          patient {
            firstName
            lastName
            gender
            dob
            barcode
            sampleReceived
            tests {
              type
              diseasePanel
              testedDiseaseCount
              conditions
              reportDate
            }
          }
          receivingProvider {
            name
            clinicName
            clinicLocation
          }
          orderingProvider
          orderingClinic
          billingInfo {
            description
            primaryInsuranceCard {
              insuranceProvider
              patientFirstName
              patientLastName
              patientAddress1
              patientAddress2
              patientCity
              patientState
              patientPostalCode
              patientDob
              relationshipToInsured
              memberId
              groupNumber
            }
            secondaryInsuranceCard {
              insuranceProvider
              patientFirstName
              patientLastName
              patientAddress1
              patientAddress2
              patientCity
              patientState
              patientPostalCode
              patientDob
              relationshipToInsured
              memberId
              groupNumber
            }
            partnerPrimaryInsuranceCard {
              insuranceProvider
              patientFirstName
              patientLastName
              patientAddress1
              patientAddress2
              patientCity
              patientState
              patientPostalCode
              patientDob
              relationshipToInsured
              memberId
              groupNumber
            }
            partnerSecondaryInsuranceCard {
              insuranceProvider
              patientFirstName
              patientLastName
              patientAddress1
              patientAddress2
              patientCity
              patientState
              patientPostalCode
              patientDob
              relationshipToInsured
              memberId
              groupNumber
            }
            creditCard {
              name
              type
              last4
              expMonth
              expYear
            }
          }
        }
      }
    }
  }
` as TypedDocumentNode<GetOrderDetailsQuery, GetOrderDetailsQueryVariables>;

export const GET_ORDER_REQUEST_RESULTS_RELEASED = gql`
  query GetOrderRequestResultsReleased($externalId: String!) {
    orderDetails(externalId: $externalId) {
      __typename
      ... on OrderDetailsSuccess {
        detail {
          hasReleasedResults
        }
      }
    }
  }
` as TypedDocumentNode<
  GetOrderRequestResultsReleasedQuery,
  GetOrderRequestResultsReleasedQueryVariables
>;

export default GET_ORDERS;
